import { FC, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import {
  Box,
  Button,
  TextField,
  Popover,
  InputAdornment,
  IconButton,
} from "@mui/material";
import "react-day-picker/dist/style.css";
import Icon from "../../components/Icon";
import { formatDate } from "../../utils/dateFunctions";

const DATE_PICKER_CSS = `
  .rdp {
    --rdp-cell-size: 40px;
    --rdp-accent-color: #1976d2;
    --rdp-background-color: #e6f4ff;
    margin: 0;
  }
  .rdp-day_selected:not(.rdp-day_start):not(.rdp-day_end):not(.rdp-day_outside) {
    background-color: var(--rdp-background-color);
    color: var(--rdp-accent-color);
  }
  .rdp-day_selected {
    background-color: var(--rdp-accent-color);
  }
  .rdp-day:hover:not(.rdp-day_selected) {
    background-color: #f5f5f5;
  }
`;

interface IMuiDatePicker {
  mode?: "range" | "single";
  handleRangeChange?: (range?: DateRange) => void;
  handleDateChange?: (date?: Date) => void;
  value?: Date[] | Date;
  placeholder?: string;
}

const MuiDatePicker: FC<IMuiDatePicker> = ({
  mode = "range",
  handleRangeChange,
  handleDateChange,
  value,
  placeholder = "Select date range",
}) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    mode === "range" && Array.isArray(value)
      ? { from: value[0], to: value[1] }
      : undefined,
  );

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    mode === "single" && !Array.isArray(value) ? value : undefined,
  );

  const formatDateRange = (dates?: Date[]) => {
    if (!dates?.length) return "";
    return dates.length === 1
      ? formatDate(dates[0])
      : `${formatDate(dates[0])} - ${formatDate(dates[1])}`;
  };

  const formatSingleDate = (date?: Date) => {
    return date ? formatDate(date) : "";
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = () => {
    if (mode === "range" && handleRangeChange) {
      handleRangeChange(selectedRange);
    } else if (mode === "single" && handleDateChange) {
      handleDateChange(selectedDate);
    }
    handleClose();
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mode === "range") {
      setSelectedRange(undefined);
      handleRangeChange?.(undefined);
    } else {
      setSelectedDate(undefined);
      handleDateChange?.(undefined);
    }
    handleClose();
  };

  return (
    <Box>
      <TextField
        value={
          mode === "range"
            ? formatDateRange(Array.isArray(value) ? value : undefined)
            : formatSingleDate(!Array.isArray(value) ? value : undefined)
        }
        onClick={handleClick}
        size="small"
        placeholder={placeholder}
        name={mode === "range" ? "date-range" : "single-date"}
        label="Value"
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: value && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleReset} edge="end">
                  <Icon name="close" width={16} height={16} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <style>{DATE_PICKER_CSS}</style>
        {mode === "range" ? (
          <DayPicker
            mode="range"
            selected={selectedRange}
            footer={
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button variant="text" color="primary" onClick={handleSelect}>
                  Select
                </Button>
              </Box>
            }
            onSelect={setSelectedRange}
          />
        ) : (
          <DayPicker
            mode="single"
            selected={selectedDate}
            footer={
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button variant="text" color="primary" onClick={handleSelect}>
                  Select
                </Button>
              </Box>
            }
            onSelect={setSelectedDate}
          />
        )}
      </Popover>
    </Box>
  );
};

export default MuiDatePicker;
